import React, { createContext, useContext, useState, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  toggleTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const savedMode = localStorage.getItem('themeMode');
    return (savedMode as ThemeMode) || 'dark';
  });

  const toggleTheme = () => {
    setMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', newMode);
      return newMode;
    });
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: mode === 'light' ? '#111' : '#fff',
            light: mode === 'light' ? '#444' : '#ccc',
            dark: mode === 'light' ? '#000' : '#fff',
          },
          secondary: {
            main: mode === 'light' ? '#e0e0e0' : '#333',
            light: mode === 'light' ? '#f5f5f5' : '#444',
            dark: mode === 'light' ? '#bdbdbd' : '#222',
          },
          background: {
            default: mode === 'light' ? '#fff' : '#121212',
            paper: mode === 'light' ? '#fafbfc' : '#1e1e1e',
          },
          text: {
            primary: mode === 'light' ? '#111' : '#fff',
            secondary: mode === 'light' ? '#444' : '#ccc',
          },
          divider: mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                borderRadius: 0,
                padding: '12px 24px',
                fontWeight: 600,
              },
              contained: {
                background: mode === 'light' ? '#fff' : '#1e1e1e',
                color: mode === 'light' ? '#111' : '#fff',
                border: `1px solid ${mode === 'light' ? '#111' : '#fff'}`,
                boxShadow: 'none',
                '&:hover': {
                  background: mode === 'light' ? '#f5f5f5' : '#2e2e2e',
                  boxShadow: 'none',
                },
              },
              outlined: {
                border: `1px solid ${mode === 'light' ? '#111' : '#fff'}`,
                color: mode === 'light' ? '#111' : '#fff',
                background: mode === 'light' ? '#fff' : '#1e1e1e',
                '&:hover': {
                  background: mode === 'light' ? '#f5f5f5' : '#2e2e2e',
                },
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
                backgroundColor: mode === 'light' ? '#fff' : '#1e1e1e',
                borderRadius: 0,
                border: `1px solid ${mode === 'light' ? '#e0e0e0' : '#333'}`,
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 0,
                border: `1px solid ${mode === 'light' ? '#e0e0e0' : '#333'}`,
                background: mode === 'light' ? '#fff' : '#1e1e1e',
                boxShadow: 'none',
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'light' ? '#fff' : '#1e1e1e',
                color: mode === 'light' ? '#111' : '#fff',
                boxShadow: 'none',
                borderBottom: `1px solid ${mode === 'light' ? '#e0e0e0' : '#333'}`,
              },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                backgroundColor: mode === 'light' ? '#fafbfc' : '#1e1e1e',
                borderRight: `1px solid ${mode === 'light' ? '#e0e0e0' : '#333'}`,
              },
            },
          },
          MuiListItemButton: {
            styleOverrides: {
              root: {
                '&:hover': {
                  backgroundColor: mode === 'light' ? '#f5f5f5' : '#2e2e2e',
                },
              },
            },
          },
          MuiAvatar: {
            styleOverrides: {
              root: {
                background: mode === 'light' ? '#fafbfc' : '#2e2e2e',
                color: mode === 'light' ? '#111' : '#fff',
              },
            },
          },
          MuiDivider: {
            styleOverrides: {
              root: {
                borderColor: mode === 'light' ? '#e0e0e0' : '#333',
              },
            },
          },
        },
      }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}; 